import { useContext, useState, useCallback } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, ResponsiveFlex, Button } from "@narmi/design_system";
import { entities, cents } from "byzantine";
import { useNotificationContext, catcat as cc } from "cerulean";
import Account from "byzantine/src/Account";
import filters from "byzantine/src/filters";
import { parsedRowsToSubmitPayload } from "byzantine/src/dbbl/businessLogic/entities/wireImport/helpers";
import Container from "../../Container";
import HeaderWithSteps from "../../HeaderWithSteps";
import AccountContext from "../../contexts/AccountContext";
import { RowWrapper } from "../CreateBulk/ReviewBulkScreen";
import styles from "../../transfer/ach/ReviewScreen.module.scss";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useDomesticWireImportContext } from "./DomesticWireImportContext";
import WireReviewPaymentDialog from "./WireReviewPaymentDialog";
import { downloadWireImportCSV } from "./helpers";

const WireReviewScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { accounts } = useContext(AccountContext);
  const { sendNotification } = useNotificationContext();
  const { csvResponse, parsedRows, paymentDetails, validPayments, totalAmount } =
    useDomesticWireImportContext();
  const { submit, loading } = entities.wireImport.useSubmitWireImport();

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const formattedTotal = filters.currency(
    cents(Math.round(totalAmount * 100))
  );
  const totalPayments = validPayments.length;

  const senderAccountData = accounts.find(
    (account: Account) => account.id === paymentDetails.fromAccount
  );
  const senderAccount = new Account(senderAccountData || {});

  const handleDownloadFile = useCallback(async () => {
    if (!csvResponse) return;
    try {
      await downloadWireImportCSV(csvResponse.s3_key, csvResponse.original_filename);
    } catch {
      sendNotification({ type: "negative", text: l10n.getString("wire-review-download-error") });
    }
  }, [csvResponse, l10n, sendNotification]);

  // Redirect if context is empty (direct URL access)
  if (!parsedRows || !csvResponse) {
    window.location.assign(`/${PAYMENT_ACTIVITY_ROUTE}/upload`);
    return null;
  }

  const handleGoBack = () => {
    navigate("../payments");
  };

  const handleClickSubmit = async () => {
    const submitRows = parsedRowsToSubmitPayload(parsedRows);

    if (submitRows.length === 0) {
       sendNotification({
         type: "negative",
         text: l10n.getString("wire-payments-table-error-no-payments"),
       });
       return;
     }

    const payload: API.WireImport.Submit.Request = {
      s3_key: csvResponse.s3_key,
      from_account: paymentDetails.fromAccount as API.AccountId,
      rows: submitRows,
    };

    if (paymentDetails.description) {
      payload.description = paymentDetails.description;
    }
    if (paymentDetails.internalNote) {
      payload.note = paymentDetails.internalNote;
    }

    try {
      await submit(payload);
      navigate("../receipt");
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("wire-review-submit-error"),
      });
    }
  };

  const toggleReviewDialog = () => {
    setReviewDialogOpen(!reviewDialogOpen);
  };

  return (
    <Container>
      <ResponsiveFlex gapSize="xl">
        <HeaderWithSteps
          headerText={l10n.getString("review-your-payment-heading")}
        />

        <div className={styles.reviewCardContainer}>
          <div className={styles.reviewCardHeader}>
            <h1>{`-${formattedTotal}`}</h1>
          </div>

          <div className={styles.reviewCardSection}>
              <div className="margin--bottom--xs">
                <RowWrapper
                  heading={l10n.getString("wire-review-sending-label")}
                  content={l10n.getString(
                    "wire-review-payment-count",
                    {
                      total: formattedTotal,
                      count: totalPayments,
                    }
                  )}
                />
              </div>
              <Row alignItems="center">
                <Row.Item shrink>
                  <p className="fontColor--secondary">
                    {l10n.getString("wire-review-details-label")}
                  </p>
                </Row.Item>
                <Row.Item>
                  <div className={styles.reviewRowContent}>
                    <Button
                      kind="plain"
                      size="m"
                      onClick={toggleReviewDialog}
                      testId="toggle-wire-review-dialog"
                    >
                      {l10n.getString("wire-review-view-payments", {
                        total: totalPayments,
                      })}
                      <span className="narmi-icon-chevron-right" />
                    </Button>
                  </div>
                </Row.Item>
              </Row>
              <Row alignItems="center">
                <Row.Item shrink>
                  <p className="fontColor--secondary">
                    {l10n.getString("label-file")}
                  </p>
                </Row.Item>
                <Row.Item>
                  <div className={styles.reviewRowContent}>
                    <Button
                      kind="plain"
                      size="m"
                      onClick={handleDownloadFile}
                      testId="wire-review-download-file"
                    >
                      <span className="narmi-icon-download" />
                      <span className="margin--left--xs">{l10n.getString("label-download-file")}</span>
                    </Button>
                  </div>
                </Row.Item>
              </Row>
              <p className="margin--top--xs">{l10n.getString("wire-review-note")}</p>
          </div>

          <div className={cc([styles.reviewCardSectionBordered, "padding--bottom--xs"])}>
            <ResponsiveFlex gapSize="m">
              <h5 className="fontSize--xs fontWeight--bold margin--top--m">
                {l10n
                  .getString("wire-review-details-heading")
                  .toUpperCase()}
              </h5>
              <RowWrapper
                heading={l10n.getString(
                  "wire-payment-details-payment-title-label"
                )}
                content={paymentDetails.description}
              />
              <RowWrapper
                heading={l10n.getString("label-review-date-once")}
                content={DateTime.now().toFormat("MM/dd/yyyy")}
              />
              <RowWrapper
                heading={l10n.getString("label-originating-account")}
                content={senderAccount.getShortDescription()}
              />
              {paymentDetails.internalNote && (
                <RowWrapper
                  heading={l10n.getString(
                    "label-internal-note"
                  )}
                  content={paymentDetails.internalNote}
                />
              )}
            </ResponsiveFlex>
          </div>
        </div>

        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                label={l10n.getString("button-back")}
                onClick={handleGoBack}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label={l10n.getString("button-submit")}
                onClick={handleClickSubmit}
                disabled={loading}
              />
            </Row.Item>
          </Row>
        </div>
      </ResponsiveFlex>
      <WireReviewPaymentDialog
        isOpen={reviewDialogOpen}
        handleClose={toggleReviewDialog}
        payments={validPayments}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
      />
    </Container>
  );
};

export default WireReviewScreen;
